import { Section } from '@/pages/CompanyReport/components/Overview/components/Section';
import { SubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSectionHeader';
import track from 'react-tracking';
import { CompetitorWrapper } from '../styled';
import { useQuery } from '@tanstack/react-query';
import {
  getPeersQueryKey,
  queryPeersFn,
} from '@/pages/CompanyReport/components/Valuation/query';
import { toCurrencyTruncated } from '@/utilities/formatting';
import CompetitorSkeleton from '../Components/Competitor/CompetitorSkeleton';
import { useDwellTracking } from '@/pages/CompanyReport/components/DwellTrackingProvider';
import { useReportSection } from '@/pages/CompanyReport/components/ReportSectionProvider';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { useLocale } from '@/hooks/useLocale';
import { useTranslation } from '@/hooks/useTranslation';
import Competitor from '@/pages/CompanyReport/components/SimilarCompanies/Components/Competitor';

interface PeerCompaniesProps {
  canonicalURL: string;
  name: string;
  section: string;
}

function PeerCompanies({ canonicalURL, name }: PeerCompaniesProps) {
  const { data: peersList } = useQuery({
    queryKey: getPeersQueryKey(canonicalURL),
    queryFn: queryPeersFn,
    staleTime: Infinity,
    enabled: Boolean(canonicalURL),
  });
  const { observe } = useDwellTracking();
  const { section } = useReportSection();
  const locale = useLocale();

  const t = useTranslation();

  return (
    <Section
      data-cy-id="overview-competitors"
      data-section={section}
      ref={observe}
    >
      <SubSectionHeader data-cy-id="competitors-title">
        {name ? (
          t('page.companyReport.components.peerCompanies.heading', {
            companyName: name,
          })
        ) : (
          <>&nbsp;</>
        )}
      </SubSectionHeader>
      <CompetitorWrapper
        justifyContentMd={
          peersList === undefined || peersList.Company.peers.length === 4
            ? 'space-between'
            : 'flex-start'
        }
        noWrap
      >
        {peersList === undefined || peersList.Company.peers.length === 0 ? (
          <>
            <CompetitorSkeleton />
            <CompetitorSkeleton />
            <CompetitorSkeleton />
            <CompetitorSkeleton />
          </>
        ) : (
          peersList.Company.peers.map((n, i) => {
            const marketCap = n.company.analysisValue.marketCap;
            const points = Object.values(n.company.score || {}).map(
              (score) => score! + 1
            );
            return (
              <Competitor
                key={`competitor-holding-${n.companyId + i}`}
                index={i}
                points={points}
                companyId={n.companyId}
                companyName={n.company.name}
                uniqueSymbol={n.company.uniqueSymbol}
                marketCap={
                  marketCap
                    ? toCurrencyTruncated(
                        marketCap,
                        1,
                        n.company.listingCurrencyISO
                      )
                    : 'n/a'
                }
                companyUrl={toLocaleUrl(locale, n.company.canonicalURL)}
              />
            );
          })
        )}
      </CompetitorWrapper>
    </Section>
  );
}

export default track((props: PeerCompaniesProps) => ({
  type: 'track',
  subject: `${props.section}_related_companies`,
}))(PeerCompanies) as typeof PeerCompanies;
